// Header.jsx (Dark theme + refreshed content/design)
import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaBars, FaTimes, FaPhoneAlt, FaArrowRight, FaBolt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import { resolveSiteName, SITE_LOGO_URL } from "../../utils/siteConfig";

const drawerAnim = {
  hidden: { x: 48, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.26, ease: "easeOut" } },
  exit: { x: 48, opacity: 0, transition: { duration: 0.2, ease: "easeIn" } },
};

const overlayAnim = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Work", path: "/services" },
      { label: "Process", path: "/our-strategies" },
      { label: "Contact", path: "/contact" },
    ],
    []
  );

  const phoneCall = webinfo?.phonecall || webinfo?.phone || "";
  const phoneText = webinfo?.phone || webinfo?.phonecall || "Call";
  const brand = resolveSiteName(webinfo);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`hdr ${scrolled ? "hdr--scrolled" : ""}`}>
      <div className="hdr__row">
        {/* Brand */}
        <div className="hdr__brand" onClick={() => go("/")} role="button" tabIndex={0}>
          <img className="hdr__logo" src={SITE_LOGO_URL} alt={brand} />
        </div>

        {/* Desktop */}
        <div className="hdr__nav" aria-label="Header navigation">
          <div className="hdr__links" role="navigation" aria-label="Main navigation">
            {navItems.map((it) => (
              <button
                key={it.path}
                className={`hdr__link ${isActive(it.path) ? "hdr__link--active" : ""}`}
                onClick={() => go(it.path)}
              >
                {it.label}
              </button>
            ))}
          </div>

          <div className="hdr__actions">
            <button
              className="hdr__btn hdr__btn--ghost"
              onClick={() => (window.location.href = phoneCall ? `tel:${phoneCall}` : "#")}
              title="Call"
            >
              <FaPhoneAlt className="hdr__btnIcon" />
              <span>{phoneText}</span>
            </button>

            <button className="hdr__btn hdr__btn--primary" onClick={() => go("/contact")}>
              <FaBolt className="hdr__btnIcon" />
              Start a Project <FaArrowRight className="hdr__btnIcon" />
            </button>
          </div>
        </div>

        {/* Mobile trigger */}
        <button className="hdr__burger" onClick={() => setOpen(true)} aria-label="Open menu">
          <FaBars />
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="hdr__overlay"
              variants={overlayAnim}
              initial="hidden"
              animate="show"
              exit="exit"
              onClick={() => setOpen(false)}
            />

            <motion.aside
              className="hdr__drawer"
              variants={drawerAnim}
              initial="hidden"
              animate="show"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile menu"
            >
              <div className="hdr__drawerTop">
                <div className="hdr__drawerBrand">
                  <img className="hdr__logo hdr__logo--sm" src={SITE_LOGO_URL} alt={brand} />
                </div>

                <button className="hdr__close" onClick={() => setOpen(false)} aria-label="Close menu">
                  <FaTimes />
                </button>
              </div>

              <div className="hdr__drawerLinks">
                {navItems.map((it) => (
                  <button
                    key={it.path}
                    className={`hdr__mLink ${isActive(it.path) ? "hdr__mLink--active" : ""}`}
                    onClick={() => go(it.path)}
                  >
                    <span>{it.label}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </div>

              <div className="hdr__drawerBottom">
                <div className="hdr__help">
                  <b>Quick response:</b> Share your project details — we'll send a proposal within{" "}
                  <b>24 hours</b>.
                </div>

                <div className="hdr__drawerBtns">
                  <button
                    className="hdr__btn hdr__btn--ghost"
                    onClick={() => (window.location.href = phoneCall ? `tel:${phoneCall}` : "#")}
                  >
                    <FaPhoneAlt /> Call {phoneText}
                  </button>

                  <button className="hdr__btn hdr__btn--primary" onClick={() => go("/contact")}>
                    <FaBolt className="hdr__btnIcon" />
                    Start a Project <FaArrowRight className="hdr__btnIcon" />
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;